import { DatePipe } from '@angular/common';
import { Component, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TableColumn } from 'src/@vex/interfaces/table-column.interface';
import { User } from 'src/core/interfaces/user';
import { Permissions } from 'src/assets/static-data/permissions';
import { MatTableDataSource } from '@angular/material/table';
import { UntypedFormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from 'src/app/shared/services/user.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { TransactionsInternalServicesService } from '../../../../../../../src/app/shared/services/transactions-internal-services.service';
import Swal from 'sweetalert2';
import { SalesDeliveryStatus } from 'src/assets/static-data/enums/sales-delivery-status.enum';
import { ISalesDelivery } from 'src/core/interfaces/ISalesDelivery';
import { SalesDeliveryService } from 'src/app/shared/services/sales-delivery.service';
import { Subject, takeUntil } from 'rxjs';


@UntilDestroy()
@Component({
  selector: 'app-sales-delivery',
  templateUrl: './sales-delivery.component.html',
  styleUrls: ['./sales-delivery.component.scss','../transactions.component.scss'],
  providers: [DatePipe] // Provide DatePipe

})
export class SalesDeliveryComponent {
  
  // Public properties
  SalesDeliveryStatus = SalesDeliveryStatus; // Expose the enum to the template
  ISalesDelivery: ISalesDelivery[];
  permissions = new Permissions();
  currentUser: User;
  isLoading = false;
  currentPage = 0;
  isRtl: boolean;
  typingTimer: any;
  doneTypingInterval = 800; // Delay in ms
  pageSize = 10;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100, 250, 500, 1000, 5000, 10000];
  dataSource: MatTableDataSource<ISalesDelivery> | null;
  totalRows = 0;
  searchCtrl = new UntypedFormControl();

  // Table Columns
  columns: TableColumn<ISalesDelivery>[] = [
    { label: this.translateService.instant("TRANSACTIONUMBER.TEXT"), property: 'transactionNumber', type: 'text', visible: true },
    { label: this.translateService.instant("TRANSACTIONDATE.TEXT"), property: 'transactionDate', type: 'text', visible: true },
    { label: this.translateService.instant("CUSTOMERCODE.TEXT"), property: 'customerCode', type: 'text', visible: true },
    { label: this.translateService.instant("CUSTOMERNAME.TEXT"), property: 'customerName', type: 'text', visible: true },
    { label: this.translateService.instant("STATUS.TEXT"), property: 'status', type: 'text', visible: true },
    { label: this.translateService.instant("ACTIONS.TEXT"), property: 'actions', type: 'button', visible: true },
  ];

  private destroy$ = new Subject<void>();

  // ViewChild properties
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;

  // Constructor with injected services
  constructor(
    private dialog: MatDialog,
    private userService: UserService,
    private authService: AuthService,
    private translateService: TranslateService,
    private SalesDeliveryService: SalesDeliveryService,
    private router: Router,
    private datePipe: DatePipe,
    private salesDeliveryInternalService: TransactionsInternalServicesService
  ) {}

  // Lifecycle hooks
  ngOnInit() {
    this.dataSource = new MatTableDataSource();
    this.getSalesDelivery();
    this.isRtl = this.translateService.currentLang === 'ar'|| this.translateService.currentLang == 'ur';
    this.currentUser = this.authService.currentUserValue;
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  ngOnDestroy() {
    // Emit destroy signal to unsubscribe all
    this.destroy$.next();
    this.destroy$.complete();
  }

  // API call to fetch sales delivery data
  getSalesDelivery(criteria = null) {
    this.isLoading = true;
    this.SalesDeliveryService.getPaginatedSalesDeliveries(this.currentPage, this.pageSize, true, criteria).pipe(takeUntil(this.destroy$)).subscribe(response => {
      this.isLoading = false;
      this.dataSource.data = response.data.items;
      this.dataSource.data.forEach((row) => {
        // Convert status value to its key
        row.status = this.salesDeliveryInternalService.getStatusKeyFromValue(row.status, 2) as SalesDeliveryStatus;
        row.transactionDate = this.datePipe.transform(row.transactionDate, 'dd/MM/yyyy');
      });

      // Update paginator settings
      setTimeout(() => {
        this.paginator.pageIndex = this.currentPage;
        this.paginator.length = response.data.count;
      });
    });
  }

  // Event handler for page changes
  pageChanged(event: PageEvent) {
    this.pageSize = event.pageSize;
    this.currentPage = event.pageIndex;
    this.getSalesDelivery();
  }

  // Event handler for typing finished
  onTypingFinished(value: string) {
    this.onFilterChange(value);
  }

  // Keyup event handler for search input
  onKeyup(event: any) {
    clearTimeout(this.typingTimer);
    this.typingTimer = setTimeout(() => {
      this.onTypingFinished(event.target.value);
    }, this.doneTypingInterval);
  }

  // Filter change handler
  onFilterChange(value: string) {
    if (!this.dataSource) {
      return;
    }
    this.currentPage = 0;
    value = value.trim().toLowerCase();
    this.getSalesDelivery(value);
  }

  // Toggle column visibility
  toggleColumnVisibility(column, event) {
    event.stopPropagation();
    event.stopImmediatePropagation();
    column.visible = !column.visible;
  }

  // Get visible columns for the table
  get visibleColumns() {
    return this.columns.filter(column => column.visible).map(column => column.property);
  }

  // Navigate to the view page
  reviewRow(delivery: any) {
    this.router.navigate(['/transactions/sales-deliveries/view', delivery.id]);
  }

  // Get CSS classes based on status
  getStatusClasses(status: string): { textClass: string; bgClass: string } | undefined {
    return this.salesDeliveryInternalService.getStatusClasses(status, 2);
  }

  // Track by function for *ngFor
  trackByProperty<T>(index: number, column: TableColumn<T>) {
    return column.property;
  }

}
